import { useState, type ReactNode } from "react";
import { Check, Copy, Link2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import { Section } from "./ui/section";
import { usePublicGroupInvite, useSetInviteEnabled } from "../api/hooks";
import type { Group } from "../api/types";
import { useAppConfig } from "../config/AppConfig";

interface InviteLinkCardProps {
  group: Group;
}

/**
 * OWNER-only card that surfaces the public invite URL and an enable / disable
 * toggle. The URL is always derivable from `groupId`, so disabled-then-
 * re-enabled invites keep the same link — exactly the user's "single
 * shareable link" model.
 *
 * Initial state is inferred from the public-read endpoint: a successful
 * response means the invite is enabled; an error (404) means it's disabled.
 */
export function InviteLinkCard({ group }: InviteLinkCardProps): ReactNode {
  const inviteQ = usePublicGroupInvite(group.id);
  // Local override holds the OWNER's most recent successful toggle so the
  // UI doesn't flicker through the next refetch. Cleared by the query state
  // each time it changes — see the derived `enabled` below.
  const [override, setOverride] = useState<boolean | null>(null);

  const enabled: boolean | null =
    override !== null
      ? override
      : inviteQ.isSuccess
        ? true
        : inviteQ.isError
          ? false
          : null;

  const [copied, setCopied] = useState(false);
  const setInvite = useSetInviteEnabled(group.id);
  const { webBaseUrl } = useAppConfig();

  const url = `${webBaseUrl}/groups/${group.id}/join`;

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard permission denied — leave the URL visible for manual copy.
    }
  }

  async function toggle(): Promise<void> {
    if (enabled === null) return;
    const next = !enabled;
    try {
      const result = await setInvite.mutateAsync({ enabled: next });
      setOverride(result.enabled);
    } catch {
      // Mutation surfaces its own error state below; keep the previous
      // local toggle so the UI doesn't lie about the server state.
    }
  }

  if (enabled === null) {
    return (
      <Section title="Invite link">
        <Skeleton className="h-32" />
      </Section>
    );
  }

  return (
    <Section
      title="Invite link"
      description="Anyone with this link can preview the group. They'll need to sign in and request to join before they can take part."
    >
      <div className="space-y-2">
        <Label htmlFor="invite-url">Public link</Label>
        <div className="flex items-center gap-2">
          <Input
            id="invite-url"
            value={url}
            readOnly
            disabled={!enabled}
            className="font-mono text-label-md"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={copy}
            disabled={!enabled}
          >
            {copied ? (
              <>
                <Check className="size-4" /> Copied
              </>
            ) : (
              <>
                <Copy className="size-4" /> Copy
              </>
            )}
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-label-sm text-on-surface-variant">
          {enabled
            ? "Anyone with the link can preview the group."
            : "The link is disabled. Visitors get a 404."}
        </p>
        <Button
          type="button"
          size="sm"
          variant={enabled ? "ghost" : "default"}
          onClick={() => void toggle()}
          disabled={setInvite.isPending}
        >
          <Link2 className="size-4" />
          {enabled ? "Disable" : "Enable"}
        </Button>
      </div>
      {setInvite.error ? (
        <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
          Couldn't update the invite link.
        </div>
      ) : null}
    </Section>
  );
}
